import { Link } from "react-router-dom";

function LegalLayout({ title, lastUpdated, children, testId }) {
  return (
    <div className="overflow-x-hidden" data-testid={testId}>
      <section className="pt-32 pb-16" style={{ background: "linear-gradient(135deg, #091F2C 0%, #194449 55%, #1E5C60 100%)" }}>
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-2 text-sm mb-5" style={{ color: '#B9DCD2' }}>
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <span className="text-white">{title}</span>
          </div>
          <h1 className="text-4xl font-light text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h1>
          {lastUpdated && <p className="text-sm" style={{ color: '#B9DCD2' }}>Last updated: {lastUpdated}</p>}
        </div>
      </section>
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-16">
        {children}
      </div>
    </div>
  );
}

const Section = ({ num, title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-semibold text-gray-900 mb-4 mt-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
      {num && <span className="text-ms-blue mr-2">{num}.</span>}{title}
    </h2>
    {children}
  </div>
);
const P = ({ children }) => <p className="text-gray-600 leading-relaxed mb-4 text-sm">{children}</p>;

export default function EventsUpCookiePolicy() {
  return (
    <LegalLayout title="Technofocus Cookie Policy for EventsUp Websites" lastUpdated="17 November 2025" testId="eventsup-cookie-policy-page">
      <Section num="1" title="Purpose">
        <P>This Cookie Policy explains how <a href="https://technofocus.ai" className="text-ms-blue hover:underline">Technofocus Pte. Ltd.</a> ("Technofocus," "we," "our," or "us") uses cookies and similar technologies on the event websites we create and host using the Cvent platform (e.g., web.cvent.com/event/...).</P>
        <P>It describes the types of cookies placed, their purposes, and how you can control them.</P>
      </Section>

      <Section num="2" title="Who We Are">
        <P>Technofocus Pte. Ltd. is the data controller for attendee information collected on these event websites.</P>
        <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 mb-4">
          <p className="font-semibold text-gray-900 text-sm mb-2">Our contact for privacy matters:</p>
          <p className="text-gray-500 text-sm">Email: <a href="mailto:privacy@technofocus.co" className="text-ms-blue hover:underline">privacy@technofocus.co</a></p>
          <p className="text-gray-500 text-sm">Address: 138 Robinson Road, #26-01 Oxley Tower, Singapore 068906</p>
        </div>
        <P><a href="https://www.cvent.com/" target="_blank" rel="noopener noreferrer" className="text-ms-blue hover:underline">Cvent Inc.</a> acts as our data processor and provides the event-management SaaS platform. See <a href="https://www.cvent.com/en/cvent-global-privacy-policy" target="_blank" rel="noopener noreferrer" className="text-ms-blue hover:underline">Cvent Global Privacy Policy</a> and <a href="https://www.cvent.com/en/appcookies" target="_blank" rel="noopener noreferrer" className="text-ms-blue hover:underline">Cvent Cookie List</a>.</P>
      </Section>

      <Section num="3" title="What Are Cookies?">
        <P>Cookies are small text files that a website stores on your device to support basic functions and collect usage information. Some are essential for operation; others help us analyze performance or improve content.</P>
      </Section>

      <Section num="4" title="Types of Cookies We Use">
        <div className="overflow-x-auto mb-6">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-700">Category</th>
                <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-700">Purpose</th>
                <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-700">Examples</th>
                <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-700">Retention</th>
              </tr>
            </thead>
            <tbody>
              {[
                { cat: "Strictly Necessary", purpose: "Required for core site functionality such as page navigation, form submission, and session management.", examples: "eventguestside-service-session", ret: "Session-only" },
                { cat: "Performance", purpose: "Measure site traffic and performance (e.g., Dynatrace monitoring). Enabled only after consent.", examples: "_dd_s", ret: "≈ 15 minutes" },
                { cat: "Functionality", purpose: "Remember user preferences such as language or region.", examples: "cvent_functionality_xxx (if enabled)", ret: "Up to 1 year" },
                { cat: "Marketing / Analytics", purpose: "Track engagement to improve communications and campaigns. Used only after consent.", examples: "unique_visitor_tracking", ret: "Up to 13 months" },
              ].map((row) => (
                <tr key={row.cat} className="hover:bg-gray-50">
                  <td className="border border-gray-200 px-4 py-3 font-medium text-gray-800">{row.cat}</td>
                  <td className="border border-gray-200 px-4 py-3 text-gray-600">{row.purpose}</td>
                  <td className="border border-gray-200 px-4 py-3 text-gray-500 font-mono text-xs">{row.examples}</td>
                  <td className="border border-gray-200 px-4 py-3 text-gray-600">{row.ret}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section num="5" title="How We Obtain Consent">
        <P>When you first visit a Technofocus EventsUp event website, a cookie banner appears that explains the cookie types used, allows you to Accept all, Accept essential only, or Customize preferences, and blocks non-essential cookies until you choose. Your preferences are stored by Cvent in a first-party cookie and can be changed any time via the "Cookie Preferences" link in the footer.</P>
      </Section>

      <Section num="6" title="Managing or Withdrawing Consent">
        <P>You may withdraw consent for transactional emails at any time via the "opt-out" link in our email footer or cookie permissions via our cookie banner or manage cookies in your browser settings. Disabling cookies may limit site functions such as auto-fill or form progress.</P>
      </Section>

      <Section num="7" title="Third-Party Cookies">
        <P>Some cookies may be set by Cvent or its analytics partners. These third parties act on our behalf under data-processing agreements consistent with GDPR and Singapore PDPA.</P>
      </Section>

      <Section num="8" title="Legal Basis">
        <ul className="list-disc list-inside space-y-2 text-gray-600 text-sm mb-4 ml-4">
          <li>Strictly necessary cookies: processed under our legitimate interest in providing a functional website.</li>
          <li>All other cookies: processed only with your explicit consent, which you may withdraw at any time.</li>
        </ul>
      </Section>

      <Section num="9" title="Data Retention">
        <P>Cookies persist for the durations listed in the table above or until deleted by you. Consent records are retained within Cvent for up to 13 months to demonstrate compliance with GDPR and Microsoft SSPA requirements.</P>
      </Section>

      <Section num="10" title="Updates">
        <P>We may update this Cookie Policy from time to time to reflect changes in cookies or applicable laws. Any updates will be posted here with a new "Last updated" date. This policy applies only to Technofocus event websites hosted on the Cvent platform.</P>
        <P>For the full Technofocus privacy policy, please see our <Link to="/privacy" className="text-ms-blue hover:underline">Privacy Statement</Link>. For the EventsUp privacy notice, see <Link to="/eventsup-privacy" className="text-ms-blue hover:underline">EventsUp Privacy Notice</Link>.</P>
      </Section>
    </LegalLayout>
  );
}
